import {
	ToggleButton as TB,
	ToggleButtonGroup as TBG,
	type ToggleButtonGroupProps as TBGProps,
	type ToggleButtonProps as TBProps
} from '@mui/material';
import { Icon, type IconName } from '../icon';
import { modeSwitchStyles } from './mode-switch-styles';

export const MuiToggleButton = (props: TBProps) => {
	return <TB {...props} />;
};

export const MuiToggleButtonGroup = (props: TBGProps) => {
	return <TBG {...props} />;
};

export type MuiToggleButtonProps = TBProps;
export type MuiToggleButtonGroupProps = TBGProps;

export interface ModeSwitchProps<T extends string> {
	opts: Record<T, string>;
	icons?: Partial<Record<T, IconName>>;
	iconOnly?: boolean;
	ariaLabel?: string;
	value?: T;
	setValue: (a: T) => void;
	/** When enabled, prevents the value from being cleared when the user clicks the currently selected option without choosing another. */
	disableClearable?: boolean;

	fullWidth?: boolean;
	/** Small is 36px; medium is 42px. */
	size?: TBProps['size'];
	orientation?: TBGProps['orientation'];
	disabled?: boolean;
	disabledOptions?: readonly T[];
}

export function ModeSwitch<T extends string>(props: ModeSwitchProps<T>) {
	return (
		<TBG
			aria-label={props.ariaLabel}
			value={props.value}
			onChange={(_e, v) => {
				if (v === null && props.disableClearable) return;
				props.setValue(v);
			}}
			exclusive
			fullWidth={props.fullWidth}
			size={props.size ?? 'small'}
			orientation={props.orientation}
			disabled={props.disabled}
			sx={theme => modeSwitchStyles(theme, props.size ?? 'small')}
		>
			{Object.entries<string>(props.opts).map(([k, v]) => {
				const icon = props.icons?.[k as T];
				return (
					<TB
						key={`toggle-${k}`}
						disabled={props.disabled || props.disabledOptions?.includes(k as T)}
						value={k}
						aria-label={props.iconOnly ? v : undefined}
					>
						{icon ? <Icon iconName={icon} /> : null}
						{props.iconOnly && icon ? null : v}
					</TB>
				);
			})}
		</TBG>
	);
}
